import ToggleSwitch from "./../ToggleSwitch";
import { coupon } from "./../../types/globalTypes";
import CouponsEditModal from "./CouponsEditModal";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import axios from "axios";
import { useState, useRef, useEffect } from "react";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { HiDotsVertical } from "react-icons/hi";
import { AppConfig } from "@/utils/AppConfig";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/utils/helpers";

type CouponCardProps = Omit<coupon, "userId"> & {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const CouponCard: React.FC<CouponCardProps> = ({
  _id,
  active,
  amount,
  code,
  endDate,
  startDate,
  type,
  limit,
  limitPerUser,
  expired,
  leftUses,
  setNeedRefetch,
  limitExceeded
}) => {
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );


  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(active);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropDownButtonRef = useRef<HTMLButtonElement>(null);

    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const isExpiredCoupon = expired === true || limitExceeded === true


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !dropDownButtonRef.current?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    } else {
      setTimeout(() => {
        setIsDropdownOpen(true);
      }, 0);
    }
  };

  const handleToggleActiveApi = async () => {
    // if(expired) return toast.error(t("couponCard.Please_edit_the_expiration_date_first"));

    setIsLoading(true);
    const LoadingToast = toast.loading(
      t("couponCard.editingStatus") as string,);
    try {
      const url = `${BASE_URL}/api/dashboard/coupons/${_id}?locale=${lang}`;
      const response = await axios.put(
        url,
        { active: !isActive },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (!response.data.success) {
        toast.error(
          response.data.error ||
            (t("couponCard.editStatusFailed" ) as string),
        );
        return;
      }

      toast.success(
        t("couponCard.editStatusSuccess" )as string,
      );
      setIsActive(!isActive);
      setNeedRefetch(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.error ||
          (t("couponCard.settingsUpdateError") as string);
        toast.error(errorMessage);
      } else {
        toast.error(
          t("couponCard.unexpectedError") as string,
        );
      }
      console.error(
        t("couponCard.editStatusError") as string,
      );
    } finally {
      setIsLoading(false);
      toast.dismiss(LoadingToast);
    }
  };


  return (
    <tr className="border-b bg-white hover:bg-gray-50">
      <td className=" px-6 py-4 text-sm font-medium text-gray-800">
        {/* Make this to fit the statuses i send you using compare date with start and end date which comes in the form of ms */}
        <div className="flex items-center gap-2">
        <span
          className={`${t("mt-[2px]", "mb-1")} h-4 w-4 rounded-md ${ isExpiredCoupon ? "bg-yellow-500" :
            isActive ? "bg-green-500" : "bg-gray-500"
          }`}
          ></span>
            <p className="mb-1">{code}</p>
          </div>
      </td>

      <td className="px-6 py-4 text-sm text-gray-600">
        {type === "percentage" && <span dir="ltr">{`${amount} %`}</span>}
        {type === "fixed" && (
          <span dir="rtl">
            {`${formatMoney(amount)} `} {AppConfig.currency.icon()}
          </span>
        )}
      </td>

      {/* Convert Date From S to MS */}
      <td className="px-6 py-4 text-sm text-gray-600">
        {new Date(+startDate * 1000).toLocaleDateString("en-GB")}
      </td>
      <td className="px-6 py-4 text-sm text-gray-600">
        {new Date(+endDate * 1000).toLocaleDateString("en-GB")}
      </td>

      <td className="px-6 py-4 text-sm text-gray-600 text-center">
        <p><span className="font-bold">{limit === "infinite"? t("coupons.unlimited"): limit ?? "-"}</span></p>
        <p className="p-1 bg-gray-200 rounded-md m-1">{`${t("coupons.remainingUses")} : `}
          <span className="font-semibold">{leftUses === "infinite"? t("coupons.unlimited"): leftUses ?? "-"}</span>
          </p>
        </td>

      <td className="px-6 py-4 text-sm text-gray-600 text-center">
        <p><span className="font-bold">{limitPerUser ?? "-"}</span></p>
      </td>

      <td className="px-6 py-4 text-sm">
        <ToggleSwitch
          size="small"
          checked={isActive}
          onChange={handleToggleActiveApi}
          disabled={isLoading}
        />
      </td>
      <td className="px-6 py-4 text-sm text-gray-600">
        <button
          onClick={toggleDropdown}
          ref={dropDownButtonRef}
          type="button"
          className={`relative col-start-2 col-end-3 row-start-1 row-end-2 flex w-[30px] items-center justify-center gap-3 px-1 py-2 text-black ${isDropdownOpen ? "rounded-md bg-sky-50" : ""}`}
        >
          <HiDotsVertical />
          {isDropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute start-[37px] top-[-65%] z-[9999] flex h-[65px] w-[35px] cursor-default flex-col rounded-md border border-gray-200 bg-gray-100 shadow-md"
            >
              <span className="flex h-[32.5px] items-center rounded-t-md text-sm text-black hover:bg-sky-100">
                <CouponsEditModal
                  _id={_id}
                  amount={amount}
                  code={code}
                  endDate={+endDate * 1000}
                  startDate={+startDate * 1000}
                  type={type}
                  limitPerUser={limitPerUser}
                  limit={limit}
                  setNeedRefetch={setNeedRefetch}
                />
              </span>
              <span className="flex h-[32.5px] items-center rounded-b-md text-sm text-black hover:bg-sky-100">
                <DeleteConfirmationModal
                  setNeedRefetch={setNeedRefetch}
                  _id={_id}
                />
              </span>
            </div>
          )}
        </button>
      </td>
    </tr>
  );
};

export default CouponCard;
